import React, { useState, useEffect } from 'react';
import { Trash2, ExternalLink, Check, X, AlertTriangle, LogOut, RefreshCw, UserCheck, UserX, ShieldCheck, Mail, MoreVertical } from 'lucide-react';

const API_BASE = 'https://worldreach.io/wp-json/wr-affiliates/v1';

interface Affiliate {
  id: number;
  name: string;
  email: string;
  website: string | null;
  status: string;
  earnings: number;
  joined: string;
  appointmentScheduled?: boolean;
}

interface SalesPerson {
  id: number;
  name: string;
  email: string;
  referral_code: string;
}

export default function AffiliatesManagement() {
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
  const [salesPerson, setSalesPerson] = useState<SalesPerson | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const [deleteModal, setDeleteModal] = useState<Affiliate | null>(null);
  const [notification, setNotification] = useState<{ message: string; type: string } | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('wr_sales_token'));
  const [activeTab, setActiveTab] = useState('My Affiliates');

  // Login state
  const [showLogin, setShowLogin] = useState(!token);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  const showNotification = (message: string, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const apiRequest = async (endpoint: string, options: RequestInit = {}) => {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    };

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers: { ...headers, ...options.headers },
    });

    if (response.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('wr_sales_token');
      setToken(null);
      setShowLogin(true);
      throw new Error('Session expired. Please login again.');
    }

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'API request failed');
    }

    return data;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');

    try {
      const data = await fetch(`${API_BASE}/sales/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password: loginPassword }),
      }).then(r => r.json());

      if (data.success && data.token) {
        localStorage.setItem('wr_sales_token', data.token);
        setToken(data.token);
        setSalesPerson(data.sales_person);
        setShowLogin(false);
        showNotification(`Bienvenida, ${data.sales_person.name}!`);
      } else {
        setLoginError(data.message || 'Login failed');
      }
    } catch (err: any) {
      setLoginError(err.message || 'Connection error');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('wr_sales_token');
    setToken(null);
    setSalesPerson(null);
    setShowLogin(true);
    setAffiliates([]);
  };

  const fetchAffiliates = async () => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      const data = await apiRequest('/sales/affiliates?_t=' + Date.now());
      setAffiliates(data.affiliates || []);

      // Also fetch profile if not set
      if (!salesPerson) {
        const profileData = await apiRequest('/sales/me');
        setSalesPerson(profileData.sales_person);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token && !showLogin) {
      fetchAffiliates();
    }
  }, [token, showLogin]);

  const handleActivate = async (id: number) => {
    try {
      await apiRequest(`/sales/affiliates/${id}/activate`, { method: 'PUT' });
      setAffiliates(prev => prev.map(a => a.id === id ? { ...a, status: 'Active' } : a));
      setOpenDropdown(null);
      const affiliate = affiliates.find(a => a.id === id);
      showNotification(`${affiliate?.name} activated successfully`);
    } catch (err: any) {
      showNotification(err.message, 'error');
    }
  };

  const handleDeactivate = async (id: number) => {
    try {
      await apiRequest(`/sales/affiliates/${id}/deactivate`, { method: 'PUT' });
      setAffiliates(prev => prev.map(a => a.id === id ? { ...a, status: 'Inactive' } : a));
      setOpenDropdown(null);
      const affiliate = affiliates.find(a => a.id === id);
      showNotification(`${affiliate?.name} deactivated`);
    } catch (err: any) {
      showNotification(err.message, 'error');
    }
  };

  const handleVerify = async (id: number) => {
    try {
      await apiRequest(`/sales/affiliates/${id}/verify`, { method: 'PUT' });
      setAffiliates(prev => prev.map(a => a.id === id ? { ...a, status: 'Verified' } : a));
      setOpenDropdown(null);
      const affiliate = affiliates.find(a => a.id === id);
      showNotification(`${affiliate?.name} verified successfully`);
    } catch (err: any) {
      showNotification(err.message, 'error');
    }
  };

  const handleResendVerification = async (id: number) => {
    try {
      await apiRequest(`/sales/affiliates/${id}/resend-verification`, { method: 'POST' });
      setOpenDropdown(null);
      const affiliate = affiliates.find(a => a.id === id);
      showNotification(`Verification email sent to ${affiliate?.email}`);
    } catch (err: any) {
      showNotification(err.message, 'error');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await apiRequest(`/sales/affiliates/${id}`, {
        method: 'DELETE',
      });

      const affiliate = affiliates.find(a => a.id === id);
      setAffiliates(prev => prev.filter(a => a.id !== id));
      setDeleteModal(null);
      showNotification(`${affiliate?.name} ha sido eliminado`, 'delete');
    } catch (err: any) {
      showNotification(err.message, 'error');
    }
  };

  const StatusBadge = ({ status }: { status: string }) => {
    const statusLower = status.toLowerCase();
    return (
      <span className={`
        inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium
        ${statusLower === 'active' || statusLower === 'verified'
          ? 'bg-green-100 text-green-700'
          : statusLower === 'inactive'
          ? 'bg-red-100 text-red-700'
          : 'bg-amber-100 text-amber-700'
        }
      `}>
        {status}
      </span>
    );
  };

  const ActionsMenu = ({ affiliate }: { affiliate: Affiliate }) => {
    const isOpen = openDropdown === affiliate.id;
    const statusLower = affiliate.status.toLowerCase();

    return (
      <div className="relative">
        <button
          onClick={() => setOpenDropdown(isOpen ? null : affiliate.id)}
          className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
          title="Actions"
        >
          <MoreVertical className="w-4 h-4" />
        </button>

        {isOpen && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setOpenDropdown(null)} />
            <div className="absolute top-full right-0 mt-1 z-20 bg-white rounded-lg shadow-xl border border-slate-200 py-1 min-w-[200px]">
              {statusLower !== 'active' && (
                <button
                  onClick={() => handleActivate(affiliate.id)}
                  className="w-full px-4 py-2 text-left text-sm flex items-center gap-3 hover:bg-green-50 text-slate-700 hover:text-green-700 transition-colors"
                >
                  <UserCheck className="w-4 h-4" />
                  Activate User
                </button>
              )}
              {statusLower === 'active' && (
                <button
                  onClick={() => handleDeactivate(affiliate.id)}
                  className="w-full px-4 py-2 text-left text-sm flex items-center gap-3 hover:bg-red-50 text-slate-700 hover:text-red-700 transition-colors"
                >
                  <UserX className="w-4 h-4" />
                  Deactivate User
                </button>
              )}
              {statusLower !== 'verified' && (
                <button
                  onClick={() => handleVerify(affiliate.id)}
                  className="w-full px-4 py-2 text-left text-sm flex items-center gap-3 hover:bg-blue-50 text-slate-700 hover:text-blue-700 transition-colors"
                >
                  <ShieldCheck className="w-4 h-4" />
                  Verify User
                </button>
              )}
              <button
                onClick={() => handleResendVerification(affiliate.id)}
                className="w-full px-4 py-2 text-left text-sm flex items-center gap-3 hover:bg-slate-50 text-slate-700 transition-colors"
              >
                <Mail className="w-4 h-4" />
                Resend Verification Email
              </button>
              <div className="border-t border-slate-100 my-1" />
              <button
                onClick={() => { setOpenDropdown(null); setDeleteModal(affiliate); }}
                className="w-full px-4 py-2 text-left text-sm flex items-center gap-3 hover:bg-red-50 text-red-600 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Delete User
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  // Login form
  if (showLogin) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
          <div className="text-center mb-8">
            <span className="text-cyan-500 font-bold text-2xl tracking-tight">WORLD<span className="text-slate-900">REACH</span></span>
            <h1 className="text-xl font-semibold text-slate-900 mt-4">Sales Portal</h1>
            <p className="text-slate-500 text-sm mt-2">Ingresa con tu cuenta de vendedor</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            {loginError && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                {loginError}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
              <input
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-slate-900 bg-white"
                placeholder="tu@email.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
              <input
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-slate-900 bg-white"
                placeholder="Tu contraseña"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loginLoading}
              className="w-full py-3 bg-cyan-500 text-white font-medium rounded-lg hover:bg-cyan-600 transition-colors disabled:opacity-50"
            >
              {loginLoading ? 'Ingresando...' : 'Ingresar'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-cyan-400 font-bold text-lg tracking-tight">WORLD<span className="text-white">REACH</span></span>
            <span className="text-slate-400">|</span>
            <span className="text-slate-300 text-sm">Sales Portal v2</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-slate-300 text-sm">Welcome, {salesPerson?.name || 'User'}</span>
            <button
              onClick={handleLogout}
              className="px-4 py-1.5 text-sm border border-red-500 text-red-500 rounded hover:bg-red-500 hover:text-white transition-colors flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-8">
            {['Overview', 'My Affiliates', 'Deals', 'Payments', 'Tools & Codes'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab
                    ? 'border-cyan-500 text-slate-900'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Overview Tab */}
        {activeTab === 'Overview' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h3 className="text-sm font-medium text-slate-500 mb-2">Total Affiliates</h3>
              <p className="text-3xl font-bold text-slate-900">{affiliates.length}</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h3 className="text-sm font-medium text-slate-500 mb-2">Pending Approval</h3>
              <p className="text-3xl font-bold text-amber-500">{affiliates.filter(a => a.status.toLowerCase() === 'pending').length}</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h3 className="text-sm font-medium text-slate-500 mb-2">Total Earnings</h3>
              <p className="text-3xl font-bold text-green-600">${affiliates.reduce((sum, a) => sum + a.earnings, 0).toFixed(2)}</p>
            </div>
          </div>
        )}

        {/* My Affiliates Tab */}
        {activeTab === 'My Affiliates' && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
              <h1 className="text-lg font-semibold text-slate-900">My Affiliates ({affiliates.length})</h1>
              <button
                onClick={fetchAffiliates}
                disabled={loading}
                className="p-2 text-slate-400 hover:text-cyan-500 hover:bg-cyan-50 rounded-lg transition-all"
                title="Refresh"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {error && (
              <div className="px-6 py-4 bg-red-50 border-b border-red-100">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            {loading && affiliates.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <RefreshCw className="w-8 h-8 text-slate-400 animate-spin mx-auto mb-4" />
                <p className="text-slate-500">Cargando afiliados...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50">
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Website</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Earnings</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Joined</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {affiliates.map((affiliate) => (
                      <tr key={affiliate.id} className="hover:bg-slate-50/50 transition-colors">
                        <td className="px-6 py-4">
                          <span className="text-sm font-medium text-slate-900">{affiliate.name}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-slate-600">{affiliate.email}</span>
                        </td>
                        <td className="px-6 py-4">
                          {affiliate.website ? (
                            <a
                              href={affiliate.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-sm text-cyan-600 hover:text-cyan-700 hover:underline"
                            >
                              View
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          ) : (
                            <span className="text-sm text-slate-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <StatusBadge status={affiliate.status} />
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-slate-900 font-medium">
                            ${affiliate.earnings.toFixed(2)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-slate-600">{affiliate.joined}</span>
                        </td>
                        <td className="px-6 py-4">
                          <ActionsMenu affiliate={affiliate} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!loading && affiliates.length === 0 && (
              <div className="px-6 py-12 text-center">
                <p className="text-slate-500">No hay afiliados registrados</p>
              </div>
            )}
          </div>
        )}

        {/* Deals Tab */}
        {activeTab === 'Deals' && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
            <h2 className="text-lg font-semibold text-slate-900 mb-2">Deals</h2>
            <p className="text-slate-500">Coming soon - View and manage your deals here</p>
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'Payments' && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
            <h2 className="text-lg font-semibold text-slate-900 mb-2">Payments</h2>
            <p className="text-slate-500">Coming soon - View your payment history and earnings</p>
          </div>
        )}

        {/* Tools & Codes Tab */}
        {activeTab === 'Tools & Codes' && salesPerson && (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl p-6 text-white">
              <h2 className="text-lg font-semibold mb-2">Your Referral Link</h2>
              <div className="bg-white/20 rounded-lg p-3 flex items-center justify-between">
                <code className="text-sm">https://app.worldreach.io/register?ref={salesPerson.referral_code}</code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(`https://app.worldreach.io/register?ref=${salesPerson.referral_code}`);
                    showNotification('Link copied!');
                  }}
                  className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded text-sm transition-colors"
                >
                  Copy
                </button>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Your Referral Code</h3>
              <div className="bg-slate-100 rounded-lg p-4 flex items-center justify-between">
                <code className="text-2xl font-bold text-cyan-600">{salesPerson.referral_code}</code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(salesPerson.referral_code);
                    showNotification('Code copied!');
                  }}
                  className="px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors"
                >
                  Copy Code
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Referral Link Card - show on Overview and My Affiliates */}
        {(activeTab === 'Overview' || activeTab === 'My Affiliates') && salesPerson && (
          <div className="mt-6 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl p-6 text-white">
            <h2 className="text-lg font-semibold mb-2">Tu link de referidos</h2>
            <div className="bg-white/20 rounded-lg p-3 flex items-center justify-between">
              <code className="text-sm">https://app.worldreach.io/register?ref={salesPerson.referral_code}</code>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`https://app.worldreach.io/register?ref=${salesPerson.referral_code}`);
                  showNotification('Link copiado!');
                }}
                className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded text-sm transition-colors"
              >
                Copiar
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Delete Modal */}
      {deleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 animate-in zoom-in-95 duration-200">
            <div className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Eliminar afiliado</h3>
                  <p className="text-sm text-slate-500">Esta accion no se puede deshacer</p>
                </div>
              </div>
              <p className="text-slate-600 mb-6">
                Estas seguro de que deseas eliminar a <span className="font-semibold text-slate-900">{deleteModal.name}</span>?
                Se eliminaran todos sus datos y registro de ganancias.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setDeleteModal(null)}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => handleDelete(deleteModal.id)}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                >
                  Si, eliminar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification Toast */}
      {notification && (
        <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-bottom-4 fade-in duration-300">
          <div className={`
            px-4 py-3 rounded-lg shadow-lg flex items-center gap-3
            ${notification.type === 'delete'
              ? 'bg-slate-900 text-white'
              : notification.type === 'error'
              ? 'bg-red-600 text-white'
              : 'bg-blue-600 text-white'
            }
          `}>
            {notification.type === 'delete' ? (
              <Trash2 className="w-4 h-4" />
            ) : notification.type === 'error' ? (
              <X className="w-4 h-4" />
            ) : (
              <Check className="w-4 h-4" />
            )}
            <span className="text-sm font-medium">{notification.message}</span>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slide-in-from-top-2 {
          from { transform: translateY(-8px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes slide-in-from-bottom-4 {
          from { transform: translateY(16px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes zoom-in-95 {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .animate-in {
          animation-fill-mode: both;
        }
        .fade-in {
          animation-name: fade-in;
        }
        .slide-in-from-top-2 {
          animation-name: fade-in, slide-in-from-top-2;
        }
        .slide-in-from-bottom-4 {
          animation-name: fade-in, slide-in-from-bottom-4;
        }
        .zoom-in-95 {
          animation-name: fade-in, zoom-in-95;
        }
        .duration-200 {
          animation-duration: 200ms;
        }
        .duration-300 {
          animation-duration: 300ms;
        }
      `}</style>
    </div>
  );
}
